/**
 * PmSetup — Jennifer / Shannon edit the PM's day guide and the Do / Don't
 * list (Sep 11, 2026). Gated client-side to the setup tier and server-side by
 * the pmt_write / pmr_write RLS policies (pm_can_edit_setup()).
 *
 * Guide items: label · kind (check / photo / note / return email) · required ·
 * hint · section. Photo items have NO count — any photo in the section
 * completes it (Greg, Sep 11). ▲▼ moves an item through the whole
 * ordered list (so an item can cross sections). "Hide" deactivates — job
 * history keeps pointing at the row, nothing is deleted.
 * Rules: two ordered lists (Do / Don't), ▲▼ within a list, ⇄ swaps lists.
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import { useRealtimeTable } from "../../lib/useRealtimeTable";
import {
  fetchRules, fetchTemplates, saveRule, saveRuleOrder, saveTemplate, saveTemplateOrder, setRuleActive, setTemplateActive,
  type PmRule, type PmTaskKind, type PmTemplate, type PmTemplateInput,
} from "../../lib/pmBoard";

const KIND_LABEL: Record<PmTaskKind, string> = {
  check: "Checkbox",
  photo: "Photos",
  note: "Note",
  return_email: "SRS return email",
};

type SetupTab = "guide" | "rules";

export default function PmSetup({ memberId }: { memberId: string | null }) {
  const [tab, setTab] = useState<SetupTab>("guide");
  const [templates, setTemplates] = useState<PmTemplate[]>([]);
  const [rules, setRules] = useState<PmRule[]>([]);
  const [showHidden, setShowHidden] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [flash, setFlash] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const [t, r] = await Promise.all([fetchTemplates(true), fetchRules(true)]);
      setTemplates(t); setRules(r); setErr(null);
    } catch (e) { setErr((e as Error).message); }
  }, []);
  useEffect(() => { void load(); }, [load]);
  useRealtimeTable("pm_task_templates", load);
  useRealtimeTable("pm_rules", load);

  const ok = (msg: string) => { setFlash(msg); window.setTimeout(() => setFlash(null), 2000); };
  const fail = (e: unknown) => setErr((e as Error).message);

  return (
    <div>
      <div className="mb-3 flex items-center gap-2">
        <div className="flex rounded-lg border border-slate-200 bg-white p-0.5 text-xs font-semibold">
          {(["guide", "rules"] as SetupTab[]).map((t) => (
            <button key={t} onClick={() => setTab(t)} className={`rounded-md px-3 py-1.5 ${tab === t ? "bg-orange-500 text-white" : "text-slate-600"}`}>
              {t === "guide" ? "Day guide" : "Do / Don't"}
            </button>
          ))}
        </div>
        <label className="ml-auto flex items-center gap-1.5 text-xs text-slate-600">
          <input type="checkbox" checked={showHidden} onChange={(e) => setShowHidden(e.target.checked)} /> show hidden
        </label>
      </div>
      {err && <div className="mb-3 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">{err}</div>}
      {flash && <div className="mb-3 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-700">{flash}</div>}

      {tab === "guide" ? (
        <GuideEditor templates={templates} showHidden={showHidden} memberId={memberId} onOk={ok} onFail={fail} onReload={load} />
      ) : (
        <RulesEditor rules={rules} showHidden={showHidden} memberId={memberId} onOk={ok} onFail={fail} onReload={load} />
      )}
      <p className="mt-6 text-[11px] text-slate-400">
        Changes land on every PM's phone live. Hiding an item keeps the history on jobs that already used it.
      </p>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Guide editor                                                        */
/* ------------------------------------------------------------------ */

const EMPTY: PmTemplateInput = { section: "", label: "", kind: "check", min_photos: 1, required: true, hint: "" };

function GuideEditor({ templates, showHidden, memberId, onOk, onFail, onReload }: {
  templates: PmTemplate[]; showHidden: boolean; memberId: string | null;
  onOk: (m: string) => void; onFail: (e: unknown) => void; onReload: () => void;
}) {
  const visible = useMemo(() => templates.filter((t) => showHidden || t.is_active), [templates, showHidden]);
  const sections = useMemo(() => Array.from(new Set(templates.filter((t) => t.is_active).map((t) => t.section))), [templates]);
  const [editing, setEditing] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [busy, setBusy] = useState(false);

  const move = async (id: string, dir: -1 | 1) => {
    // Reorder within the ACTIVE ordered list (hidden items keep their slot).
    const ids = templates.filter((t) => t.is_active).map((t) => t.id);
    const i = ids.indexOf(id);
    const j = i + dir;
    if (i < 0 || j < 0 || j >= ids.length) return;
    [ids[i], ids[j]] = [ids[j], ids[i]];
    setBusy(true);
    try { await saveTemplateOrder(ids, memberId); onReload(); }
    catch (e) { onFail(e); } finally { setBusy(false); }
  };

  const hide = async (t: PmTemplate, active: boolean) => {
    setBusy(true);
    try { await setTemplateActive(t.id, active, memberId); onOk(active ? "Item restored." : "Item hidden."); onReload(); }
    catch (e) { onFail(e); } finally { setBusy(false); }
  };

  let lastSection = "";
  return (
    <div>
      <div className="space-y-2">
        {visible.map((t) => {
          const header = t.section !== lastSection;
          lastSection = t.section;
          return (
            <div key={t.id}>
              {header && <div className="mb-1 mt-4 px-1 text-xs font-extrabold uppercase tracking-wide text-slate-500">{t.section}</div>}
              {editing === t.id ? (
                <TemplateForm initial={t} sections={sections} busy={busy}
                  onCancel={() => setEditing(null)}
                  onSave={async (input) => {
                    setBusy(true);
                    try { await saveTemplate(t.id, input, memberId); onOk("Saved."); setEditing(null); onReload(); }
                    catch (e) { onFail(e); } finally { setBusy(false); }
                  }} />
              ) : (
                <div className={`flex items-start gap-2 rounded-xl border bg-white p-3 ${t.is_active ? "border-slate-200" : "border-dashed border-slate-300 opacity-60"}`}>
                  <div className="flex flex-col gap-0.5">
                    <button disabled={busy || !t.is_active} onClick={() => move(t.id, -1)} className="rounded px-2 text-slate-500 active:bg-slate-100 disabled:opacity-30" aria-label="Move up">▲</button>
                    <button disabled={busy || !t.is_active} onClick={() => move(t.id, 1)} className="rounded px-2 text-slate-500 active:bg-slate-100 disabled:opacity-30" aria-label="Move down">▼</button>
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-semibold text-slate-900">{t.label}</div>
                    <div className="mt-1 flex flex-wrap gap-1 text-[11px]">
                      <span className="rounded-full bg-slate-100 px-2 py-0.5 text-slate-600">{KIND_LABEL[t.kind]}</span>
                      <span className={`rounded-full px-2 py-0.5 ${t.required ? "bg-orange-50 text-orange-700" : "bg-slate-100 text-slate-500"}`}>{t.required ? "required" : "optional"}</span>
                      {!t.is_active && <span className="rounded-full bg-slate-200 px-2 py-0.5 text-slate-600">hidden</span>}
                    </div>
                    {t.hint && <div className="mt-1 text-xs text-slate-500">{t.hint}</div>}
                  </div>
                  <div className="flex shrink-0 flex-col gap-1">
                    <button onClick={() => setEditing(t.id)} className="rounded-lg border border-slate-200 px-2 py-1 text-xs font-semibold text-slate-700">Edit</button>
                    {t.is_active
                      ? <button disabled={busy} onClick={() => hide(t, false)} className="rounded-lg px-2 py-1 text-xs font-semibold text-rose-600">Hide</button>
                      : <button disabled={busy} onClick={() => hide(t, true)} className="rounded-lg px-2 py-1 text-xs font-semibold text-emerald-700">Restore</button>}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {adding ? (
        <div className="mt-4">
          <TemplateForm initial={{ ...EMPTY, section: sections[sections.length - 1] ?? "" }} sections={sections} busy={busy}
            onCancel={() => setAdding(false)}
            onSave={async (input) => {
              setBusy(true);
              try {
                const maxOrder = templates.reduce((m, t) => Math.max(m, t.sort_order), 0);
                await saveTemplate(null, input, memberId, maxOrder + 10);
                onOk("Item added at the end — use ▲ to move it."); setAdding(false); onReload();
              } catch (e) { onFail(e); } finally { setBusy(false); }
            }} />
        </div>
      ) : (
        <button onClick={() => setAdding(true)} className="mt-4 w-full rounded-xl border-2 border-dashed border-orange-300 bg-orange-50 px-4 py-3 text-sm font-bold text-orange-700">+ Add a guide item</button>
      )}
    </div>
  );
}

function TemplateForm({ initial, sections, busy, onSave, onCancel }: {
  initial: PmTemplateInput; sections: string[]; busy: boolean;
  onSave: (input: PmTemplateInput) => void; onCancel: () => void;
}) {
  const [f, setF] = useState<PmTemplateInput>({ ...initial, hint: initial.hint ?? "" });
  const set = (patch: Partial<PmTemplateInput>) => setF((x) => ({ ...x, ...patch }));
  const valid = f.label.trim().length > 1 && f.section.trim().length > 0;
  return (
    <div className="rounded-xl border-2 border-orange-300 bg-white p-3">
      <div className="grid gap-2 sm:grid-cols-2">
        <label className="block sm:col-span-2">
          <span className="mb-1 block text-xs font-bold uppercase tracking-wide text-slate-500">What the PM sees</span>
          <input value={f.label} onChange={(e) => set({ label: e.target.value })} placeholder="e.g. Starter strip at eaves AND rakes" className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm" />
        </label>
        <label className="block">
          <span className="mb-1 block text-xs font-bold uppercase tracking-wide text-slate-500">Section</span>
          <input list="pm-sections" value={f.section} onChange={(e) => set({ section: e.target.value })} placeholder="Arrival / Tear-off / Build…" className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm" />
          <datalist id="pm-sections">{sections.map((s) => <option key={s} value={s} />)}</datalist>
        </label>
        <label className="block">
          <span className="mb-1 block text-xs font-bold uppercase tracking-wide text-slate-500">Type</span>
          <select value={f.kind} onChange={(e) => set({ kind: e.target.value as PmTaskKind })} className="w-full rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm">
            {(Object.keys(KIND_LABEL) as PmTaskKind[]).map((k) => <option key={k} value={k}>{KIND_LABEL[k]}</option>)}
          </select>
        </label>
        <label className="flex items-center gap-2 pt-5 text-sm text-slate-700">
          <input type="checkbox" checked={f.required} onChange={(e) => set({ required: e.target.checked })} /> Required (counts toward the day's progress)
        </label>
        <label className="block sm:col-span-2">
          <span className="mb-1 block text-xs font-bold uppercase tracking-wide text-slate-500">Hint (optional)</span>
          <textarea rows={2} value={f.hint ?? ""} onChange={(e) => set({ hint: e.target.value })} placeholder="Why it matters / what to capture" className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm" />
        </label>
      </div>
      <div className="mt-3 flex gap-2">
        <button disabled={busy || !valid} onClick={() => onSave({ ...f, section: f.section.trim(), label: f.label.trim(), hint: (f.hint ?? "").trim() || null })}
          className="rounded-xl bg-slate-900 px-4 py-2 text-sm font-bold text-white disabled:opacity-50">{busy ? "Saving…" : "Save"}</button>
        <button disabled={busy} onClick={onCancel} className="rounded-xl border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700">Cancel</button>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Rules editor                                                        */
/* ------------------------------------------------------------------ */

function RulesEditor({ rules, showHidden, memberId, onOk, onFail, onReload }: {
  rules: PmRule[]; showHidden: boolean; memberId: string | null;
  onOk: (m: string) => void; onFail: (e: unknown) => void; onReload: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState<string | null>(null);
  const [draft, setDraft] = useState("");
  const [newText, setNewText] = useState<Record<"do" | "dont", string>>({ do: "", dont: "" });

  const list = (kind: "do" | "dont") => rules.filter((r) => r.kind === kind && (showHidden || r.is_active));

  const move = async (r: PmRule, dir: -1 | 1) => {
    const ids = rules.filter((x) => x.kind === r.kind && x.is_active).map((x) => x.id);
    const i = ids.indexOf(r.id); const j = i + dir;
    if (i < 0 || j < 0 || j >= ids.length) return;
    [ids[i], ids[j]] = [ids[j], ids[i]];
    setBusy(true);
    try { await saveRuleOrder(ids, memberId); onReload(); } catch (e) { onFail(e); } finally { setBusy(false); }
  };
  const swapKind = async (r: PmRule) => {
    setBusy(true);
    try {
      const kind = r.kind === "do" ? "dont" : "do";
      const maxOrder = rules.filter((x) => x.kind === kind).reduce((m, x) => Math.max(m, x.sort_order), 0);
      await saveRule(r.id, { kind, text: r.text }, memberId, maxOrder + 10); onReload();
    } catch (e) { onFail(e); } finally { setBusy(false); }
  };
  const saveText = async (r: PmRule) => {
    if (!draft.trim()) return;
    setBusy(true);
    try { await saveRule(r.id, { kind: r.kind, text: draft.trim() }, memberId); onOk("Saved."); setEditing(null); onReload(); }
    catch (e) { onFail(e); } finally { setBusy(false); }
  };
  const add = async (kind: "do" | "dont") => {
    const text = newText[kind].trim();
    if (!text) return;
    setBusy(true);
    try {
      const maxOrder = rules.filter((x) => x.kind === kind).reduce((m, x) => Math.max(m, x.sort_order), 0);
      await saveRule(null, { kind, text }, memberId, maxOrder + 10);
      setNewText((n) => ({ ...n, [kind]: "" })); onOk("Added."); onReload();
    } catch (e) { onFail(e); } finally { setBusy(false); }
  };
  const hide = async (r: PmRule, active: boolean) => {
    setBusy(true);
    try { await setRuleActive(r.id, active, memberId); onReload(); } catch (e) { onFail(e); } finally { setBusy(false); }
  };

  const Column = ({ kind }: { kind: "do" | "dont" }) => (
    <div className={`rounded-2xl border p-3 ${kind === "do" ? "border-emerald-200 bg-emerald-50/40" : "border-rose-200 bg-rose-50/40"}`}>
      <div className={`mb-2 text-sm font-extrabold ${kind === "do" ? "text-emerald-800" : "text-rose-800"}`}>{kind === "do" ? "✔ Do" : "✖ Don't"}</div>
      <div className="space-y-2">
        {list(kind).map((r) => (
          <div key={r.id} className={`flex items-start gap-2 rounded-xl border bg-white p-2 ${r.is_active ? "border-slate-200" : "border-dashed opacity-60"}`}>
            <div className="flex flex-col">
              <button disabled={busy || !r.is_active} onClick={() => move(r, -1)} className="px-1.5 text-slate-500 disabled:opacity-30" aria-label="Up">▲</button>
              <button disabled={busy || !r.is_active} onClick={() => move(r, 1)} className="px-1.5 text-slate-500 disabled:opacity-30" aria-label="Down">▼</button>
            </div>
            <div className="min-w-0 flex-1">
              {editing === r.id ? (
                <div>
                  <textarea rows={2} value={draft} onChange={(e) => setDraft(e.target.value)} className="w-full rounded-lg border border-slate-300 px-2 py-1.5 text-sm" />
                  <div className="mt-1 flex gap-2">
                    <button disabled={busy} onClick={() => saveText(r)} className="rounded-lg bg-slate-900 px-3 py-1 text-xs font-bold text-white">Save</button>
                    <button disabled={busy} onClick={() => setEditing(null)} className="rounded-lg border border-slate-300 px-3 py-1 text-xs font-semibold">Cancel</button>
                  </div>
                </div>
              ) : (
                <div className="text-sm text-slate-800">{r.text}</div>
              )}
              {editing !== r.id && (
                <div className="mt-1 flex flex-wrap gap-2 text-[11px] font-semibold">
                  <button onClick={() => { setEditing(r.id); setDraft(r.text); }} className="text-slate-600">Edit</button>
                  <button disabled={busy} onClick={() => swapKind(r)} className="text-slate-600">⇄ Move to {kind === "do" ? "Don't" : "Do"}</button>
                  {r.is_active
                    ? <button disabled={busy} onClick={() => hide(r, false)} className="text-rose-600">Hide</button>
                    : <button disabled={busy} onClick={() => hide(r, true)} className="text-emerald-700">Restore</button>}
                </div>
              )}
            </div>
          </div>
        ))}
        {list(kind).length === 0 && <div className="text-xs text-slate-400">Nothing here yet.</div>}
      </div>
      <div className="mt-2 flex gap-2">
        <input value={newText[kind]} onChange={(e) => setNewText((n) => ({ ...n, [kind]: e.target.value }))}
          onKeyDown={(e) => { if (e.key === "Enter") void add(kind); }}
          placeholder={kind === "do" ? "Add a Do…" : "Add a Don't…"} className="min-w-0 flex-1 rounded-xl border border-slate-300 px-3 py-2 text-sm" />
        <button disabled={busy || !newText[kind].trim()} onClick={() => add(kind)} className="rounded-xl bg-slate-900 px-3 py-2 text-sm font-bold text-white disabled:opacity-50">Add</button>
      </div>
    </div>
  );

  return (
    <div className="grid gap-3 sm:grid-cols-2">
      {Column({ kind: "do" })}
      {Column({ kind: "dont" })}
    </div>
  );
}
